import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import ReactTable from "components/lib/Global/ReactTable";
import { getCompanyNameWithSuffix } from "utils/company";
import { setCookie } from "utils/cookie";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import EmptyTableState from "components/lib/Shared/EmptyTableState";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { useDebounce } from "use-debounce";
import SearchInput from "components/lib/Shared/SearchInput";
import { getEsignatureEmailLink } from "utils/esignature";
import { Constants } from "config/constants";
import { getCompaniesTransactionsSignatureAsync } from "state/slices/companiesTransactions";

/**
 * SignApplications component handles the signing of applications for companies and transactions.
 * It displays a list of applications that are pending signature and allows the user to sign them.
 *
 */
const SignApplications = () => {
  const dispatch = useDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();
  const currentUser = useSelector((state) => state.userSlice);
  const [rowsPerPage] = useState(10);
  const { companiesTransactionsSignature, getCompaniesTransactionsSignature } =
    useSelector((state) => state.companiesTransactionsSlice);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  useEffect(() => {
    dispatch(
      getCompaniesTransactionsSignatureAsync({
        queryParams: {
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          search: debouncedSearchTerm,
          order: "desc",
        },
      })
    );
  }, [currentPage, debouncedSearchTerm, rowsPerPage]);

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  const columns = [
    {
      Header: "Batch No.",
      accessor: "BatchNo",
      Cell: ({ row }) => row.original?.batchNo || "-",
    },
    {
      Header: "Name.",
      accessor: "companyName1",
      Cell: ({ row }) => {
        if (row.original?.companyInfo) {
          return getCompanyNameWithSuffix(row.original.companyInfo);
        } else {
          return getCompanyNameWithSuffix(row.original);
        }
      },
    },
    {
      Header: "Application Type",
      accessor: "applicationType",

      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return row.original?.applicationType;
        } else {
          if (row.original?.transferYourBusinessInitiator) {
            return "Transfer Your Business";
          }
          return "Incorporation";
        }
      },
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        return (
          capitalize(original?.changeType) || original?.companyInfo?.type || "-"
        );
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };

        return capitalize(original?.transactionType) || "Automatic";
      },
    },
    {
      Header: "Tnx No.",
      accessor: "TnxNo",
      Cell: ({ row: { original } }) => {
        return (
          original?.trxRef || original?.companyInfo?.registrationNumber || "-"
        );
      },
    },
    {
      Header: "Date",
      Cell: ({ row: { original } }) => {
        const date = original?.companyInfo?.createdAt || original?.createdAt;
        return date ? format(new Date(date), "yyyy/MM/dd") : "N/A";
      },
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return getMaintainYourCompanyReviewStatus(row.original?.status);
        } else {
          return row.original?.status || row.original?.status;
        }
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        const handleSignatureBtnClick = () => {
          setCookie("role", 7);
          if (row.original?.applicationType) {
            let eSignatureLink = null;
            /**
             * This code handles e-signature cases
             */
            if (row?.original?.eSignatureEmailLinks?.length > 0) {
              /** Check if the logged in user has an e-signature link */
              eSignatureLink = getEsignatureEmailLink(
                currentUser,
                row.original?.eSignatureEmailLinks
              );
            }
            if (eSignatureLink) {
              history.push("/esign" + eSignatureLink.split("esign")[1]);
            } else {
              /** This is for the manual signing by uploader */
              history.push(
                `/companies/${
                  row.original?.companyInfo?._id
                }/dashboard/transactions/${row.original._id}/${
                  row.original.status ===
                    Constants.MAINTAIN_COMPANY_SIGNATURE_STATUS
                      .DIRECTOR_SIGNATURE ||
                  row.original.status ===
                    Constants.MAINTAIN_COMPANY_SIGNATURE_STATUS
                      .SHAREHOLDER_SIGNATURE
                    ? "approvals-signature"
                    : `changes-signature/${
                        row?.original?.changeType === "APPOINTMENT"
                          ? "appointment"
                          : row?.original?.changeType === "CESSATION"
                          ? "cessation"
                          : row?.original?.changeType ===
                            "CHANGE-OF-PARTICULARS"
                          ? "changeOfParticulars"
                          : ""
                      }`
                }`
              );
            }
          } else history.push(`/companies/${row.original?._id}`);
        };
        if (
          !row.original?.applicationType &&
          (row.original?.companyInfo?.envelopes?.length > 0 ||
            row.original?.envelopes?.length > 0)
        ) {
          return (
            <TableActionMenu
              row={row}
              hideDeleteBtn={true}
              hideEditBtn={true}
              showViewBtn={true}
              handleView={() => {
                history.push(
                  `/companies/${
                    row.original?.companyInfo?._id
                      ? row.original?.companyInfo?._id
                      : row.original._id
                  }/view`
                );
              }}
            />
          );
        } else {
          return (
            <button onClick={handleSignatureBtnClick}>
              <span>Sign Documents</span>
            </button>
          );
        }
      },
    },
  ];

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between my-2">
        <div className="flex items-center space-x-4">
          <h2 className="font-semibold">Filter by:</h2>
        </div>
        <SearchInput value={searchTerm} handleOnChange={handleSearch} />
      </div>
      {companiesTransactionsSignature.items.length > 0 ? (
        <div className="w-full bg-white shadow">
          <div className="w-full">
            <ReactTable
              columns={columns}
              data={companiesTransactionsSignature.items}
              currentPage={currentPage}
              totalRows={companiesTransactionsSignature.totalCount}
              rowsPerPage={rowsPerPage}
              setCurrentPage={setCurrentPage}
              paginate
              isLoading={getCompaniesTransactionsSignature.status === "loading"}
            />
          </div>
        </div>
      ) : (
        <>
          {getCompaniesTransactionsSignature.status === "loading" ? (
            <div className="z-10 flex items-center justify-center mt-10">
              <SpinnerIcon className="text-gray-400" />
            </div>
          ) : (
            <EmptyTableState>No companies</EmptyTableState>
          )}
        </>
      )}
    </div>
  );
};

export default SignApplications;
